import { FC, useEffect, useState } from 'react';
import { DragDropContext, DropResult, Droppable } from 'react-beautiful-dnd';
import { useDispatch, useSelector } from 'react-redux';

import { AlignContainer, BaseSpinner } from 'wbs-components-lib';

import { Box, Typography } from '@mui/material';

import { VipTaskModel } from '@shared/types';
import { reorder } from '@shared/utils';

import { useGetAllVipTasksQuery, useUpdateVipTaskOrderMutation } from '@services';

import { AppDispatch, getVipTasksSelector, setVipTasks } from '@store';

import { VipTaskListProps } from './types';
import { VipTaskElement } from './VipTaskElement';

import styles from './VipTaskList.module.css';

const { vipTasksList, vipTasksListContainer } = styles;

export const VipTaskList: FC<VipTaskListProps> = ({
  handleVipTaskEditModalOpen,
  handleVipTaskDeleteModalOpen,
  handleVipTaskItemEditModalOpen,
  handleVipTaskItemDeleteModalOpen,
  handleVipTaskItemCreateModalOpen,
  handleVipTaskItemExport,
  handleVipTaskItemValidateModalOpen,
}) => {
  const dispatch = useDispatch<AppDispatch>();

  const vipTasks = useSelector(getVipTasksSelector);

  const [expandedVipTaskItemsId, setExpandedVipTaskItemsId] = useState<string>('');

  const { data: allVipTasks, isLoading: isVipTasksLoading, isSuccess: isVipTasksLoaded } = useGetAllVipTasksQuery();

  const [updateVipTasksOrder] = useUpdateVipTaskOrderMutation();

  useEffect(() => {
    if (isVipTasksLoaded) {
      dispatch(setVipTasks(allVipTasks));
    }
  }, [isVipTasksLoaded, allVipTasks]);

  const handleVipTaskItemsExpand = (taskId: string) => {
    setExpandedVipTaskItemsId(taskId);
  };

  const handleEventsOrder = async (reorderedTasks: VipTaskModel[]) => {
    const orders = reorderedTasks.map((item) => {
      const { id, order } = item;

      return { id, order };
    });

    await updateVipTasksOrder({ orders });
  };

  const onDragEnd = async (result: DropResult) => {
    const { destination, source } = result;

    if (destination === null) {
      return;
    }

    const { index: destinationIndex } = destination!;
    const { index: sourceIndex } = source;

    if (destinationIndex === sourceIndex) {
      return;
    }

    const reorderedTasks = reorder<VipTaskModel>(vipTasks, sourceIndex, destinationIndex);

    dispatch(setVipTasks(reorderedTasks));

    await handleEventsOrder(reorderedTasks);
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="droppable">
        {(provided) => (
          <Box className={vipTasksList} ref={provided.innerRef} {...provided.droppableProps}>
            {!isVipTasksLoading ? (
              <>
                {vipTasks.length !== 0 ? (
                  <Box className={vipTasksListContainer}>
                    {vipTasks.map((vipTask, index) => (
                      <VipTaskElement
                        index={index}
                        vipTask={vipTask}
                        handleVipTaskItemsExpand={handleVipTaskItemsExpand}
                        expandedVipTaskItemsId={expandedVipTaskItemsId}
                        handleVipTaskEditModalOpen={handleVipTaskEditModalOpen}
                        handleVipTaskDeleteModalOpen={handleVipTaskDeleteModalOpen}
                        handleVipTaskItemEditModalOpen={handleVipTaskItemEditModalOpen}
                        handleVipTaskItemDeleteModalOpen={handleVipTaskItemDeleteModalOpen}
                        handleVipTaskItemCreateModalOpen={handleVipTaskItemCreateModalOpen}
                        handleVipTaskItemExport={handleVipTaskItemExport}
                        handleVipTaskItemValidateModalOpen={handleVipTaskItemValidateModalOpen}
                      />
                    ))}
                    {provided.placeholder}
                  </Box>
                ) : (
                  <Typography align="center" margin="50px">
                    There are no tasks at the moment. Please add a first one! 🤗
                  </Typography>
                )}
              </>
            ) : (
              <AlignContainer>
                <BaseSpinner />
              </AlignContainer>
            )}
          </Box>
        )}
      </Droppable>
    </DragDropContext>
  );
};
